import { getAccessToken } from '../utils/storage';
import { getApiUrl } from '../utils/api';

export interface ColumnInfo {
  name: string;
  data_type: string;
  is_nullable: boolean;
  column_default?: string;
}

export interface TableInfo {
  table_name: string;
  columns: ColumnInfo[];
}

export interface SchemaResponse {
  connection_id: string;
  schema: TableInfo[];
}

export interface ColumnConfigPayload {
  connection_id: number;
  selected_tables: {
    [tableName: string]: string[]; // table name -> array of column names
  };
}

export interface ColumnConfigResponse {
  success: boolean;
  message: string;
  connection_id: string;
}

export interface SensitiveColumnsResponse {
  [tableName: string]: string[]; // table name -> array of sensitive column names
}

export const fetchTenantSchema = async (connectionId: string): Promise<SchemaResponse> => {
  try {
    const accessToken = getAccessToken();
    const response = await fetch(getApiUrl(`/v1/tenant-column-config/schema/${connectionId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const data = await response.json();
    console.log('API Response:', data);

    // Handle different possible response formats
    let schema: TableInfo[];

    if (Array.isArray(data)) {
      // Response is directly an array of tables
      schema = data;
    } else if (data.schema && Array.isArray(data.schema)) {
      // Response has a schema property
      schema = data.schema;
    } else if (data.tables && Array.isArray(data.tables)) {
      // Response might have a tables property
      schema = data.tables;
    } else {
      console.error('Unexpected API response format:', data);
      throw new Error('API returned unexpected data format');
    }

    return {
      connection_id: data.connection_id || connectionId,
      schema: schema,
    };
  } catch (error) {
    console.error('Error fetching connection schema:', error);
    throw error;
  }
};

export const fetchSensitiveColumns = async (connectionId: string): Promise<SensitiveColumnsResponse> => {
  try {
    const accessToken = getAccessToken();
    const url = getApiUrl(`/v1/tenant-column-config/sensitive-columns/${connectionId}`);
    console.log('Fetching sensitive columns from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    });

    console.log('Sensitive Columns Response Status:', response.status);

    if (!response.ok) {
      // If 404, return empty object (no sensitive columns configured yet)
      if (response.status === 404) {
        console.log('No sensitive columns configuration found (404), returning empty object');
        return {};
      }
      const errorText = await response.text();
      console.error(`API Error: ${response.status}`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const data = await response.json();
    console.log('Sensitive Columns API Response (raw):', data);

    // Transform the API response to the expected format
    // API returns: { table_column_mapping: ["chat_messages.id", "orders.orderNumber", ...] }
    // We need: { "chat_messages": ["id", ...], "orders": ["orderNumber", ...] }
    const transformedData: SensitiveColumnsResponse = {};

    if (data.table_column_mapping && Array.isArray(data.table_column_mapping)) {
      data.table_column_mapping.forEach((mapping: string) => {
        // Split "table_name.column_name" into parts
        const parts = mapping.split('.');
        if (parts.length === 2) {
          const tableName = parts[0];
          const columnName = parts[1];

          if (!transformedData[tableName]) {
            transformedData[tableName] = [];
          }
          transformedData[tableName].push(columnName);
        }
      });
    }

    console.log('Transformed Sensitive Columns:', transformedData);
    return transformedData;
  } catch (error) {
    console.error('Error fetching sensitive columns:', error);
    // Return empty object on error so the page can still load with all columns selected
    return {};
  }
};

export const saveColumnConfiguration = async (
  connectionId: string,
  selectedTables: { [tableName: string]: string[] }
): Promise<ColumnConfigResponse> => {
  try {
    const accessToken = getAccessToken();
    const payload: ColumnConfigPayload = {
      connection_id: parseInt(connectionId),
      selected_tables: selectedTables,
    };

    const response = await fetch(getApiUrl('/v1/tenant-column-config/sensitive-columns/save'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ColumnConfigResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error saving column configuration:', error);
    throw error;
  }
};
